import { Injectable } from "@angular/core";
import { LineLogin } from "@awesome-cordova-plugins/line-login/ngx";
import { LoadingController, Platform } from "@ionic/angular/standalone";
import { AppConfig } from "src/app/app.config";
import { LocalStorageKey } from "src/app/local-storage-key";
import { UserProvider } from "../../api/user/user.api";
import { UserLine } from "../../models/user-line";
import { AlertProvider } from "../alert/alert.service";
import { IosAppService } from "../ios-app/ios-app.service";
import { TrackerService } from "../tracker/tracker.service";
import { Util } from "../util/util";
import { WebViewProvider } from "../web-view/web-view.service";

type ForLineRedirectURL = {
  app_id: string;
  domain: string;
};

export const PATO_PASSPORT_LIFF_ID = "1657448361-QwENGYzr";
export const PATO_PASSPORT_HAWAII_LIFF_ID = "2000119059-X4rnlpee";

@Injectable({
  providedIn: "root",
})
export class LineProvider {
  liffId: string;
  private isLineSDKInitialized = false;

  constructor(
    private tracker: TrackerService,
    private webViewProvider: WebViewProvider,
    private loadingCtrl: LoadingController,
    private alertProvider: AlertProvider,
    private patolarService: IosAppService,
    private platform: Platform,
    private userProvider: UserProvider,
    private lineLogin: LineLogin
  ) {
    this.setLiffId();
  }

  connectLine(pageName: string, isMendan = false): void {
    this.tracker.pushEventWhenLINEConnectingStart(pageName);
    if (isMendan) {
      this.webViewProvider.showLineLink(AppConfig.LINE_PATO_PASSPORT_LINK);
    } else {
      this.webViewProvider.showLineLink(AppConfig.LINE_LINK);
    }
  }

  async lineLink(): Promise<void> {
    // アプリ専用、ブラウザを除外
    if (
      AppConfig.IS_BROWSER &&
      !(Util().isPatolar() || Util().isPatoBrowser())
    ) {
      return;
    }
    if (Util().isPatolar() || Util().isPatoBrowser()) {
      const version = Util().getPatolarVersion();
      // バージョンを見る
      if (
        version &&
        ((Number(version[1]) === 1 &&
          Number(version[2]) === 1 &&
          Number(version[3]) <= 25) ||
          Number(version[1]) >= 2)
      ) {
        this.alertProvider.create({
          header: "お願い",
          subHeader:
            "LINE連携するためには、Patolarをアップデートしてください。",
          buttons: [
            {
              text: "キャンセル",
            },
            {
              text: "AppStoreへ",
              handler: () => {
                const url = "https://apps.apple.com/jp/app/id1496297563";
                this.webViewProvider.showSafariView(url);
              },
            },
          ],
        });
        return;
      }
    }

    const loading = this.loadingCtrl.create();
    (await loading).present();
    this.userProvider.getLoginToken().subscribe(
      async (res: { token: string }) => {
        if (res && res.token) {
          const url = `${AppConfig.META_DATA.serverURL}web-new/index.html?login_token=${res.token}`;
          if (Util().isPatolar() || Util().isPatoBrowser()) {
            // patolarはsafariで開く
            this.patolarService.callOpenExternalBrowser(url);
          } else {
            // それ以外は、外部ブラウザ
            this.webViewProvider.showByExternalBrowser(url);
          }
        }
        (await loading).dismiss();
      },
      async () => {
        (await loading).dismiss();
        this.alertProvider.create({
          header: "エラー",
          subHeader: "LINE連携に失敗しました",
          buttons: [
            {
              text: "OK",
            },
          ],
        });
      }
    );
  }

  // 使用端末、MemberかPatoによって、app_id、redirectURLに使用するdomainを決める関数
  decideVariableForLine(): ForLineRedirectURL {
    let domain = "";
    let app_id = AppConfig.ENV === "production" ? "1656560700" : "1656582679";
    if (AppConfig.META_DATA.appID === "pato") {
      if (AppConfig.IS_BROWSER) {
        domain = AppConfig.META_DATA.oauthLoginRedirectURLBrowser;
      } else if (this.platform.is("android")) {
        domain = AppConfig.META_DATA.oauthLoginRedirectURLAndroid;
      } else if (this.platform.is("ios")) {
        domain = AppConfig.META_DATA.oauthLoginRedirectURLiOS;
      }
    } else if (AppConfig.META_DATA.appID === "member") {
      app_id = "1657440923";
      domain = AppConfig.META_DATA.oauthLoginRedirectURLMember;
    }
    const obj: ForLineRedirectURL = {
      app_id: app_id,
      domain: domain,
    };
    return obj;
  }

  // 使用端末、MemberかPatoによって、app_id、redirectURLに使用するdomainを決める関数
  decideVariableForLineWithPatoPassport(): ForLineRedirectURL {
    let domain = "";
    const appId = AppConfig.ENV === "production" ? "1657448361" : "1657448361";
    if (AppConfig.META_DATA.appID === "pato") {
      if (AppConfig.IS_BROWSER) {
        domain = AppConfig.META_DATA.oauthLoginRedirectURLBrowser;
      } else if (this.platform.is("android")) {
        domain = AppConfig.META_DATA.oauthLoginRedirectURLAndroid;
      } else if (this.platform.is("ios")) {
        domain = AppConfig.META_DATA.oauthLoginRedirectURLiOS;
      }
    }
    const obj: ForLineRedirectURL = {
      app_id: appId,
      domain: domain + "?for_cast=true&end=true",
    };
    return obj;
  }
  // NOTE: LINEログインでURL遷移する用の処理
  startAuthLine() {
    const state = Util().random();
    localStorage.setItem(LocalStorageKey.User.StateLine, state);
    // TODO: テスト
    const forLineRedirectURL = this.decideVariableForLine();
    const domain = forLineRedirectURL.domain;
    const clientId = forLineRedirectURL.app_id;
    const scope = "profile%20openid%20email";
    const lineURL =
      "https://access.line.me/oauth2/v2.1/authorize?" +
      "response_type=code&client_id=" +
      clientId +
      "" +
      "&redirect_uri=" +
      domain +
      "&state=" +
      state +
      "&scope=" +
      scope +
      "&bot_prompt=aggressive";
    document.location.href = lineURL;
  }

  startAuthLineWithPatoPassport() {
    const state = Util().random();
    localStorage.setItem(LocalStorageKey.User.StateLine, state);
    localStorage.removeItem(LocalStorageKey.User.LINELoginType);
    const forLineRedirectURL = this.decideVariableForLineWithPatoPassport();
    const domain = forLineRedirectURL.domain;
    const clientId = forLineRedirectURL.app_id;
    const scope = "profile%20openid%20email";
    localStorage.setItem(
      LocalStorageKey.User.LINELoginType,
      "with_pato_passport"
    );
    const lineURL =
      "https://access.line.me/oauth2/v2.1/authorize?" +
      "response_type=code&client_id=" +
      clientId +
      "&redirect_uri=" +
      encodeURIComponent(domain) +
      "&state=" +
      state +
      "&scope=" +
      scope +
      "&bot_prompt=aggressive&prompt=consent";
    document.location.href = lineURL;
  }

  async startAuthLineWithPatoPassportCordova(): Promise<string | null> {
    try {
      localStorage.removeItem(LocalStorageKey.User.LINELoginType);
      localStorage.setItem(
        LocalStorageKey.User.LINELoginType,
        "with_pato_passport_cordova"
      );

      const forLineRedirectURL = this.decideVariableForLineWithPatoPassport();
      const channelId = forLineRedirectURL.app_id;

      // LINE SDKが既に初期化されているかチェック
      if (!this.isLineSDKInitialized) {
        await this.lineLogin.initialize({ channel_id: channelId });
        this.isLineSDKInitialized = true;
      }

      // loginWebを使用してWebベースのLINEログインを実行
      await this.lineLogin.login();

      // ログイン成功後にアクセストークンを取得

      const accessTokenResult = await this.lineLogin.getAccessToken();

      if (accessTokenResult && accessTokenResult.accessToken) {
        // サーバーで認証するためのアクセストークンを返却
        return accessTokenResult.accessToken;
      } else {
        this.alertProvider.create({
          header: "ログインエラー",
          subHeader: "アクセストークンの取得に失敗しました。",
          buttons: ["OK"],
        });
        throw new Error("アクセストークンの取得に失敗しました");
      }
    } catch (error) {
      // その他のエラーの場合はアラートを表示
      this.alertProvider.create({
        header: "ログインエラー",
        subHeader: "LINEログインに失敗しました。もう一度お試しください。",
        buttons: ["OK"],
      });

      return null;
    }
  }

  async startAuthLineWebWithPatoPassportCordova(): Promise<string | null> {
    try {
      localStorage.removeItem(LocalStorageKey.User.LINELoginType);
      localStorage.setItem(
        LocalStorageKey.User.LINELoginType,
        "with_pato_passport_cordova"
      );

      const forLineRedirectURL = this.decideVariableForLineWithPatoPassport();
      const channelId = forLineRedirectURL.app_id;

      // LINE SDKが既に初期化されているかチェック
      if (!this.isLineSDKInitialized) {
        await this.lineLogin.initialize({ channel_id: channelId });
        this.isLineSDKInitialized = true;
      }

      // loginWebを使用してWebベースのLINEログインを実行
      const profile = await this.lineLogin.loginWeb();
      this.alertProvider.create({
        header: "ログイン完了",
        subHeader: "LINEログインに成功しました。" + JSON.stringify(profile),
        buttons: ["OK"],
      });

      const accessTokenResult = await this.lineLogin.getAccessToken();

      if (accessTokenResult && accessTokenResult.accessToken) {
        this.alertProvider.create({
          header: "ログイン完了",
          subHeader:
            "LINEログインに成功しました。" + JSON.stringify(accessTokenResult),
          buttons: ["OK"],
        });
        // サーバーで認証するためのアクセストークンを返却
        return accessTokenResult.accessToken;
      } else {
        this.alertProvider.create({
          header: "ログインエラー",
          subHeader: "アクセストークンの取得に失敗しました。",
          buttons: ["OK"],
        });
        throw new Error("アクセストークンの取得に失敗しました");
      }
    } catch (error) {
      // その他のエラーの場合はアラートを表示
      this.alertProvider.create({
        header: "ログインエラー",
        subHeader: "LINEログインに失敗しました。もう一度お試しください。",
        buttons: ["OK"],
      });

      return null;
    }
  }

  startAuthLineDisableAutoLogin(userId: number) {
    const state = Util().random() + "_" + userId + "_disable_auto_login";
    localStorage.setItem(LocalStorageKey.User.StateLine, state);
    // TODO: テスト
    const forLineRedirectURL = this.decideVariableForLine();
    const domain = forLineRedirectURL.domain;
    const clientId = forLineRedirectURL.app_id;
    const scope = "profile%20openid%20email";
    const lineURL =
      "https://access.line.me/oauth2/v2.1/authorize?" +
      "response_type=code&client_id=" +
      clientId +
      "" +
      "&redirect_uri=" +
      domain +
      "&state=" +
      state +
      "&scope=" +
      scope +
      "&bot_prompt=aggressive" +
      "&prompt=consent&disable_auto_login=true&disable_ios_auto_login=true";
    document.location.href = lineURL;
  }

  scanQrCodeByLiff(): Promise<string> {
    return new Promise((resolve, reject) => {
      window.liff
        .scanCodeV2()
        .then((result) => {
          const stringifiedResult = result.value;
          resolve(stringifiedResult);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  }

  setLiffId(): void {
    if (Util().isLiffClient() && Util().getLiffId()) {
      // liffIdがあれば、それを使う
      this.liffId = Util().getLiffId();
      return;
    }
    let url = localStorage.getItem(LocalStorageKey.User.DeepLinks);
    // liff.state, liffClientIdがあればliffと判定する。
    if (
      Util().getQueryVariable(url, "liff.state") &&
      Util().getQueryVariable(url, "liff.state") !== "" &&
      Util().getQueryVariable(url, "liff.state").indexOf("line_liff_id") !==
        -1 &&
      Util().isLiffClient()
    ) {
      // LIFFで入ってきた場合には、hashの値を載せ替える
      url =
        AppConfig.META_DATA.hostURL +
        decodeURIComponent(Util().getQueryVariable(url, "liff.state"));
    }
    if (Util().getQueryVariable(url, "line_liff_id") !== "") {
      this.liffId = Util().getQueryVariable(url, "line_liff_id");
    } else {
      this.liffId = AppConfig.LIFF_ID;
    }
  }
  getLiffId(): string {
    return this.liffId;
  }

  // NOTE: LIFFがpatoエンタメマッチングか確認
  isPatoApp(): boolean {
    return this.getLiffId() === AppConfig.LIFF_ID;
  }

  // NOTE: LIFFがpatoエンタメマッチングか確認
  isPatoVIPCastLineApp(): boolean {
    return this.getLiffId() === AppConfig.LIFF_VIP_LINE_ID;
  }

  /** liff初期化処理 */
  initializeForLiffWithProfile(): Promise<UserLine> {
    return new Promise((resolve, reject) => {
      this.initializeForLiff().then(
        () => {
          window.liff
            .getProfile()
            .then((profile) => {
              resolve(profile);
            })
            .catch((error) => {
              reject(error);
            });
        },
        (error) => {
          reject();
        }
      );
    });
  }

  initializeForLiff(): Promise<UserLine> {
    return this.initializeForLiffId(this.getLiffId());
  }

  initializeForLiffId(liffId: string): Promise<UserLine> {
    return new Promise((resolve, reject) => {
      window.liff
        .init({
          liffId: liffId,
        })
        .then(
          () => {
            window.liff
              .getProfile()
              .then((profile) => {
                resolve(profile);
              })
              .catch((error) => {
                reject(error);
              });
          },
          (error) => {
            reject();
          }
        );
    });
  }

  openVipLineAccount(): void {
    this.webViewProvider.showLineLink("898ahmbx");
  }

  isPatoPassportLiffId(liffId: string): boolean {
    return [PATO_PASSPORT_LIFF_ID, PATO_PASSPORT_HAWAII_LIFF_ID].includes(
      liffId
    );
  }

  liffClose(): void {
    try {
      if (Util().isLiffClient()) {
        window.liff.closeWindow();
      }
    } catch (e) {
      console.log(e);
    }
  }

  liffSendMessage(msg: string) {
    try {
      if (Util().isLiffClient()) {
        window?.liff
          .sendMessages({
            type: "text",
            text: msg,
          })
          .then(() => {
            console.log("send message");
          })
          .catch((error) => {
            console.log("Error sending message: " + error);
          });
      }
    } catch (e) {
      console.log(e);
    }
  }
  liffSendImage(url: string) {
    if (Util().isLiffClient()) {
      window.liff
        .sendMessages([
          {
            type: "image",
            originalContentUrl: url,
            previewImageUrl: url,
          },
        ])
        .then(() => {
          console.log("send image" + url);
        })
        .catch((error) => {
          console.log("Error sending image: " + error);
        });
    }
  }
}
